import { Scene } from '@babylonjs/core/scene';
import { ViewpointNode } from '@models/classes/viewpoints/viewpoint-node';
import { dataURLtoBlob, downloadBlob } from '@view/helpers/blob-helper';
import { KbViewer } from '../kb-viewer';

export enum eSnapshotType {
    image = 'image',
    blob = 'blob',
    dataUrl = 'dataUrl',
}

export interface ISnapshotArgs {
    dataType: eSnapshotType;
    width: number;
    height: number;
    frameCamera?: boolean;
    framePadding?: number;
    viewpoint?: string;
    mimeType?: string; //defaults to "image/png"
    filename?: string;
}

export class SnapshotModule {
    constructor(private v: KbViewer, private scene: Scene) {}

    public take(args: ISnapshotArgs): Promise<string | Blob | void> {
        if (args.dataType == eSnapshotType.image) {
            return this.download(args);
        } else if (args.dataType == eSnapshotType.blob) {
            return this.takeAsBlob(args);
        } else {
            return this.takeAsDataUrl(args);
        }
    }

    public takeAsDataUrl(args: ISnapshotArgs): Promise<string> {
        //set the viewpoint
        let viewpointSet = false;
        if (args.viewpoint) {
            const viewpoint = this.v.sceneNode._manager!.getNodeByIdOrName<ViewpointNode>(args.viewpoint);
            if (!viewpoint) {
                throw `Could not find viewpoint ${args.viewpoint} for snapshot`;
            } else {
                this.v.camera.storeCameraSettings();
                this.v.camera.moveCameraToViewpoint(viewpoint);
                viewpointSet = true;
            }
        }

        return this.v
            .whenAssetsLoaded()
            .then(() => {
                if (args.frameCamera) {
                    this.v.camera.frameCamera(this.v.sceneNode, {
                        padding: args.framePadding,
                    });
                }
                return this.v.forceAndWaitForNextRender().then(() => {
                    return this.v.forceAndWaitForNextRender();
                });
            })
            .then(() => {
                const engine = this.v.scene.getEngine();
                const mimeType = args.mimeType || 'image/png';
                const snapshotCanvas = document.createElement('canvas');
                snapshotCanvas.width = args.width;
                snapshotCanvas.height = args.height;
                const renderContext = snapshotCanvas.getContext('2d');
                const ratio = engine.getRenderWidth() / engine.getRenderHeight();
                let newWidth = args.width;
                let newHeight = newWidth / ratio;
                if (newHeight > args.height) {
                    newHeight = args.height;
                    newWidth = newHeight * ratio;
                }
                const offsetX = Math.max(0, args.width - newWidth) / 2;
                const offsetY = Math.max(0, args.height - newHeight) / 2;
                const renderingCanvas = engine.getRenderingCanvas();
                if (renderContext && renderingCanvas) {
                    renderContext.drawImage(renderingCanvas, offsetX, offsetY, newWidth, newHeight);
                }
                const dataUrl = snapshotCanvas.toDataURL(mimeType);
                if (viewpointSet) {
                    this.v.camera.restoreCameraSettings();
                }
                snapshotCanvas.width = 0;
                snapshotCanvas.height = 0;
                return dataUrl;
            });
    }

    public takeAsBlob(args: ISnapshotArgs): Promise<Blob> {
        return this.takeAsDataUrl(args).then(dataUrl => dataURLtoBlob(dataUrl));
    }

    public download(args: ISnapshotArgs): Promise<void> {
        return this.takeAsBlob(args).then(blob => downloadBlob(blob, args.filename));
    }
}
